import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../common/Layout';
import CameraView from './CameraView';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAnemiaScore } from './useAnemiaScore';
import ProfileSelector from '../profile/ProfileSelector';
import { getCurrentLocation } from '@/utils/geolocation';
import { InferenceEngine } from 'inferencejs';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';

const ROBOFLOW_API_KEY = "9p7vUl18Wvge8FazvDkp";
// TODO: Reemplazar con tu publishable key real de Roboflow
const ROBOFLOW_PUBLISHABLE_KEY = "rf_OFsNFFtP0PcfjardIoYArLWYFlm2";

// Variable para alternar entre modelos de konjungtiva
// true = modelo 3 (serverless.roboflow.com/konjungtiva-3/1)
// false = modelo 2 (detect.roboflow.com/konjungtiva-2-gr7ta/2)
const USE_KONJUNGTIVA_MODEL_3 = false; // Por defecto modelo 2

// Sample images for demonstration
const sampleImages = {
  eyes: [
    '/eyes/ojo1.png',
    '/eyes/ojo2.png',
    '/eyes/ojo3.jpeg'
  ],
  nails: [
    '/nails/nail-1.jpg',
    '/nails/nail-2.jpg',
    '/nails/nail-3.jpg',
    '/nails/nail-4.jpg',
  ]
};

const PhotoEvaluationScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState<'profile-select' | 'eyes' | 'nails' | 'processing' | 'complete'>('profile-select');
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [eyeImage, setEyeImage] = useState<string | null>(null);
  const [nailImage, setNailImage] = useState<string | null>(null);
  const [eyesAnalysis, setEyesAnalysis] = useState<any>(null);
  const [nailsAnalysis, setNailsAnalysis] = useState<any>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [locationData, setLocationData] = useState<{latitude: number | null, longitude: number | null}>({
    latitude: null,
    longitude: null
  });
  const [session, setSession] = useState<any>(null);
  const { score, interpretation, calculateScore } = useAnemiaScore();
  const [showSamples, setShowSamples] = useState(false);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [selectedModel, setSelectedModel] = useState<'millev' | 'model2' | 'model3-server' | 'model3-live' | 'anemia-server' | 'anemia-live'>('millev');
  const [inferenceEngine, setInferenceEngine] = useState<InferenceEngine | null>(null);
  const [workerId, setWorkerId] = useState<string | null>(null);
  const [isLiveMode, setIsLiveMode] = useState(false);
  const [livePredictions, setLivePredictions] = useState<any[]>([]);

  useEffect(() => {
    const fetchSession = async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session);
      
      // We no longer redirect if not logged in
    };
    
    fetchSession();
    
    // Get current location
    const getLocation = async () => {
      const location = await getCurrentLocation();
      setLocationData({
        latitude: location.latitude,
        longitude: location.longitude
      });
      
      if (location.error) {
        console.warn("Geolocation error:", location.error);
        // Don't show error toast as it's not critical
      }
    };
    
    getLocation();
  }, [navigate]);

  // Inicializar motor de inferencia cuando se selecciona modelo Live
  useEffect(() => {
    const initializeInferenceEngine = async () => {
      if ((selectedModel === 'model3-live' || selectedModel === 'anemia-live') && !inferenceEngine) {
        try {
          console.log('Inicializando motor de inferencia...');
          const engine = new InferenceEngine();
          setInferenceEngine(engine);
          
          // Configuración común para modelos Live
          const configuration = {
            scoreThreshold: 0.3,    // Mismo que modelo 2
            iouThreshold: 0.51,     // Mismo que modelo 2 (overlap)
            maxNumBoxes: 20         // Límite de detecciones
          };
          
          let modelName, modelVersion, modelDisplayName;
          
          if (selectedModel === 'model3-live') {
            modelName = "konjungtiva-3";
            modelVersion = "1";
            modelDisplayName = "Konjungtiva 3";
          } else if (selectedModel === 'anemia-live') {
            modelName = "anemia-detection-ofvgy";
            modelVersion = "2";
            modelDisplayName = "Anemia Detection";
          }
          
          console.log(`Iniciando worker para ${modelName}...`);
          const id = await engine.startWorker(
            modelName, 
            modelVersion, 
            ROBOFLOW_PUBLISHABLE_KEY,
            configuration
          );
          setWorkerId(id);
          console.log('Inference engine initialized with worker ID:', id);
          console.log('Configuration used:', configuration);
          toast.success(`${modelDisplayName} Live inicializado correctamente`);
        } catch (error) {
          console.error('Error initializing inference engine:', error);
          toast.error('Error al inicializar el modelo Live: ' + (error as Error).message);
        }
      } else if (!['model3-live', 'anemia-live'].includes(selectedModel) && inferenceEngine) {
        // Limpiar motor cuando se cambia a otro modelo
        console.log('Limpiando motor de inferencia...');
        setInferenceEngine(null);
        setWorkerId(null);
      }
    };

    initializeInferenceEngine();
  }, [selectedModel]);

  const handleProfileSelect = (profileId: string) => {
    setSelectedProfileId(profileId);
    setStep('eyes');
  };

  // Función para manejar predicciones en tiempo real
  const handleLivePrediction = (predictions: any[]) => {
    setLivePredictions(predictions);
    console.log('Live predictions:', predictions);
  };

  const handleEyeCapture = (dataUrl: string) => {
    setEyeImage(dataUrl);
  };
  
  const handleNailCapture = (dataUrl: string) => {
    setNailImage(dataUrl);
  };
  
  const useSampleImage = (type: 'eyes' | 'nails', index: number) => {
    const convertImageToBase64 = async (imageUrl: string) => {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error converting image to base64:', error);
        toast.error(t('photoEvaluation.errors.loadSampleImage'));
        return null;
      }
    };

    const loadSampleImage = async () => {
      const base64Image = await convertImageToBase64(sampleImages[type][index]);
      if (base64Image) {
        if (type === 'eyes') {
          setEyeImage(base64Image);
        } else {
          setNailImage(base64Image);
        }
        setShowSamples(false);
      }
    };

    loadSampleImage();
  };
  
  // Función para llamar a la API de Roboflow o Millev o inferencia local
  const callRoboflowAPI = async (image: string, endpoint: string) => {
    try {
      // Si es inferencia local, usar inferencejs
      if (endpoint === 'local') {
        return await callLocalInference(image);
      }
      
      // Image is already in base64 format from the camera capture
      const base64Data = image.includes(',') ? image.split(',')[1] : image;
      
      // Configurar parámetros según el modelo
      let params;
      if (endpoint.includes('konjungtiva-2-gr7ta')) {
        // Modelo 2 - detect.roboflow.com
        params = {
          api_key: ROBOFLOW_API_KEY,
          overlap: 0.51,
          confidence: 0.3
        };
      } else if (endpoint.includes('konjungtiva-3')) {
        // Modelo 3 - serverless.roboflow.com
        params = {
          api_key: ROBOFLOW_API_KEY
        };
      } else if (endpoint.includes('anemia-detection-ofvgy')) {
        // Modelo Anemia Detection - serverless.roboflow.com
        params = {
          api_key: ROBOFLOW_API_KEY
        };
      } else {
        // Modelo de uñas u otros
        params = {
          api_key: ROBOFLOW_API_KEY
        };
      }

      const response = await axios({
        method: "POST",
        url: endpoint,
        params: params,
        data: base64Data,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error calling Roboflow:', error);
      toast.error(t('photoEvaluation.errors.analyzeImage'));
      throw error;
    }
  };

  // Función para llamar a la API de Millev
  const callMillevAPI = async (image: string) => {
    try {
      console.log('🚀 Iniciando llamada a API Millev...');
      
      // Convertir base64 a Blob
      const base64Data = image.includes(',') ? image.split(',')[1] : image;
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      // Detectar el tipo MIME de la imagen
      const mimeType = image.includes('data:image/png') ? 'image/png' : 
                       image.includes('data:image/jpeg') ? 'image/jpeg' : 
                       image.includes('data:image/jpg') ? 'image/jpeg' : 
                       'image/png';
      const blob = new Blob([bytes], { type: mimeType });
      
      console.log('📦 Blob creado:', {
        tipo: mimeType,
        tamaño: blob.size,
        tamaño_original: image.length
      });
      
      // Crear FormData
      const formData = new FormData();
      formData.append('imagen', blob, `eye-image.${mimeType === 'image/png' ? 'png' : 'jpg'}`);

      console.log('📤 Enviando petición a API Millev con fetch...');
      
      // Usar fetch nativo en lugar de axios para evitar problemas con FormData
      const response = await fetch('https://model-sangre-fuerte.millev.com/predict?return_metadata=true', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error en respuesta HTTP:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Respuesta recibida de API Millev:', data);
      return data;
    } catch (error: any) {
      console.error('❌ ERROR en llamada a API Millev:', {
        mensaje: error.message,
        tipo_error: error.name,
        error_completo: error
      });
      toast.error(t('photoEvaluation.errors.analyzeImage'));
      throw error;
    }
  };

  // Función para transformar la respuesta de Millev al formato compatible con el resto del código
  const transformMillevResponse = (millevData: any) => {
    console.log('🔄 Transformando respuesta de Millev:', millevData);
    
    // Si la clase es "Normal", no hay detección de anemia (predictions vacío)
    // Si la clase es "Anemia", creamos una predicción con la confianza
    const predictions = [];
    
    if (millevData.diagnostico?.clase === 'Anemia') {
      predictions.push({
        class: 'anemia',
        confidence: millevData.diagnostico.confianza,
        // Como Millev no devuelve bounding boxes, usamos valores por defecto centrados
        x: 50,
        y: 50,
        width: 20,
        height: 20
      });
    }
    
    const transformedData = {
      predictions: predictions,
      time: millevData.timestamp || Date.now(),
      model: 'millev-resnet50'
    };
    
    console.log('✨ Datos transformados:', transformedData);
    return transformedData;
  };

  // Función para inferencia local con inferencejs
  const callLocalInference = async (image: string) => {
    try {
      if (!inferenceEngine || !workerId) {
        throw new Error('Inference engine not initialized');
      }

      // Convertir base64 a elemento de imagen
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      return new Promise((resolve, reject) => {
        img.onload = async () => {
          try {
            // Ejecutar inferencia local con configuración opcional
            const inferenceConfig = {
              scoreThreshold: 0.3,    // Filtro de confianza
              iouThreshold: 0.51,     // Filtro de solapamiento
              maxNumBoxes: 20         // Máximo número de detecciones
            };
            const predictions = await inferenceEngine.infer(workerId, img, inferenceConfig);
            console.log('Local inference result:', predictions);
            resolve(predictions);
          } catch (error) {
            console.error('Error in local inference:', error);
            reject(error);
          }
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image for local inference'));
        };
        
        // Cargar la imagen
        img.src = image;
      });
    } catch (error) {
      console.error('Error in local inference:', error);
      throw error;
    }
  };
  
  const proceedToNails = async () => {
    // Para modo Live, usar las predicciones en tiempo real
    if (selectedModel === 'model3-live' || selectedModel === 'anemia-live') {
      if (livePredictions.length === 0) {
        toast.error('Esperando análisis en tiempo real...');
        return;
      }
      
      // Usar las predicciones en tiempo real como análisis
      setEyesAnalysis({ 
        data: { predictions: livePredictions }, 
        type: 'eyes', 
        image: eyeImage || 'live-camera' 
      });
      setStep('nails');
      return;
    }
    
    if (!eyeImage) {
      toast.error(t('photoEvaluation.errors.takeEyePhoto'));
      return;
    }
    
    try {
      setIsProcessing(true);
      
      let data;
      
      // Si es modelo Millev, usar su API
      if (selectedModel === 'millev') {
        console.log('Using Millev model for eye analysis');
        const millevResponse = await callMillevAPI(eyeImage);
        
        // Transformar respuesta de Millev a formato compatible con el resto del código
        data = transformMillevResponse(millevResponse);
      } else {
        // Seleccionar endpoint basado en el selector
        // Modelo 3 Server: https://serverless.roboflow.com/konjungtiva-3/1
        // Modelo 2: https://detect.roboflow.com/konjungtiva-2-gr7ta/2
        // Anemia Server: https://serverless.roboflow.com/anemia-detection-ofvgy/2
        let eyesEndpoint;
        if (selectedModel === 'model3-server') {
          eyesEndpoint = "https://serverless.roboflow.com/konjungtiva-3/1";
        } else if (selectedModel === 'anemia-server') {
          eyesEndpoint = "https://serverless.roboflow.com/anemia-detection-ofvgy/2";
        } else {
          eyesEndpoint = "https://detect.roboflow.com/konjungtiva-2-gr7ta/2";
        }
        
        console.log(`Using konjungtiva model: ${selectedModel}`);
        data = await callRoboflowAPI(eyeImage, eyesEndpoint);
      }
      
      setEyesAnalysis({ data, type: 'eyes' , image: eyeImage });
      console.log('Eye analysis result:', {  data, type: 'eyes' , image: eyeImage });
      
      setStep('nails');
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('photoEvaluation.errors.processEyeImage'));
    } finally {
      setIsProcessing(false);
    }
  };
  
  const processImages = async () => {
    if (!nailImage) {
      toast.error(t('photoEvaluation.errors.takeNailPhoto'));
      return;
    }
    
    try {
      setIsProcessing(true);
      setStep('processing');
      
      // Llamar directamente a la API de Roboflow para uñas
      const nailsEndpoint = "https://detect.roboflow.com/nail-disease-jmapt/1";
      const data = await callRoboflowAPI(nailImage, nailsEndpoint);
      
      const nailsAnalysisData = { data: data, type: 'nails', image: nailImage };
      setNailsAnalysis(nailsAnalysisData);
      console.log('Nail analysis result:', nailsAnalysisData);
      
      // Calcular puntuación basada en resultados del análisis
      const results = calculateScore(eyesAnalysis.data, nailsAnalysisData.data);
      
      console.log("Resultados completos:", results);
      
      // Prepare navigation state with optional user data
      const navigationState: any = { 
        ...results,
        eyesAnalysis,
        nailsAnalysis: nailsAnalysisData,
        latitude: locationData.latitude,
        longitude: locationData.longitude
      };
      
      // Only add user and profile data if logged in
      if (session && selectedProfileId) {
        navigationState.userId = session.user.id;
        navigationState.profileId = selectedProfileId;
      }
      
      navigate('/result', { state: navigationState });
      
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('photoEvaluation.errors.processImage'));
      setStep('nails');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderSampleImages = (type: 'eyes' | 'nails') => {
    return (
      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
        <h4 className="text-sm font-medium mb-2">{t('photoEvaluation.sampleImages.title')}</h4>
        <div className="grid grid-cols-3 gap-2">
          {sampleImages[type].map((src, index) => (
            <div 
              key={index}
              className="relative w-full pt-[75%] overflow-hidden rounded-md border border-gray-200 cursor-pointer hover:border-sangre-300 transition-colors"
              onClick={() => useSampleImage(type, index)}
            >
              <img 
                src={src} 
                alt={t('photoEvaluation.sampleImages.alt', { number: index + 1 })} 
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <Layout title={t('photoEvaluation.title')} showBackButton={true}>
      {step === 'profile-select' && (
        <div className="animate-slide-in-right p-4">
          <h2 className="text-xl font-semibold mb-4">{t('photoEvaluation.profileSelect.title')}</h2>
          <p className="text-gray-600 mb-6 max-w-xs">
            {t('photoEvaluation.profileSelect.description')}
          </p>
          
          <ProfileSelector 
            onSelectProfile={handleProfileSelect}
            selectedProfileId={selectedProfileId}
          />
          
          <button
            onClick={() => navigate('/profile')}
            className="btn-outline w-full mt-4 mb-4"
          >
            {t('photoEvaluation.profileSelect.manageProfiles')}
          </button>
          
          <button
            onClick={() => {
              setSelectedProfileId('guest');
              setStep('eyes');
            }}
            className="btn-primary w-full"
          >
            {t('photoEvaluation.profileSelect.continueWithoutLogin')}
          </button>
        </div>
      )}
      
      {step === 'eyes' && (
        <div className="animate-slide-in-right">
          <CameraView 
            onCapture={handleEyeCapture} 
            captureType="eyes" 
            previewImage={eyeImage}
            isLiveMode={selectedModel === 'model3-live' || selectedModel === 'anemia-live'}
            onLivePrediction={handleLivePrediction}
            inferenceEngine={inferenceEngine}
            workerId={workerId}
          />
          
          {/* Indicador de modo Live */}
          {(selectedModel === 'model3-live' || selectedModel === 'anemia-live') && (
            <div className="my-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-green-700 font-medium">
                  Modo Live: Análisis en tiempo real activo
                </span>
              </div>
              {livePredictions.length > 0 && (
                <p className="text-xs text-green-600 mt-1">
                  {livePredictions.length} detección(es) encontrada(s)
                </p>
              )}
            </div>
          )}
          
          {/* Solo mostrar imágenes de muestra si NO está en modo Live */}
          {!['model3-live', 'anemia-live'].includes(selectedModel) && (
            <div className="flex flex-col gap-2 my-4">
              <button 
                onClick={() => setShowSamples(!showSamples)}
                className="text-sm text-sangre-600 hover:text-sangre-700 flex items-center justify-center"
              >
                {showSamples ? t('photoEvaluation.sampleImages.hide') : t('photoEvaluation.sampleImages.show')}
              </button>
              
              {showSamples && renderSampleImages('eyes')}
            </div>
          )}
          
          {/* Selector de Modelo - Desplegable */}
          <div className="flex flex-col gap-2 my-4">
            <button 
              onClick={() => setShowModelSelector(!showModelSelector)}
              className="text-sm text-gray-800 hover:text-gray-900 flex items-center justify-start"
            >
              {showModelSelector ? t('photoEvaluation.modelSelector.hide') : t('photoEvaluation.modelSelector.show')}
            </button>
            
            {showModelSelector && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium mb-2">{t('photoEvaluation.modelSelector.title')}</h4>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setSelectedModel('millev')}
                    className={`w-full px-3 py-2 text-sm rounded-md transition-colors ${
                      selectedModel === 'millev'
                        ? 'bg-sangre-500 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    Millev (Recomendado)
                  </button>
                  <button
                    onClick={() => setSelectedModel('model2')}
                    className={`w-full px-3 py-2 text-sm rounded-md transition-colors ${
                      selectedModel === 'model2'
                        ? 'bg-sangre-500 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    {t('photoEvaluation.modelSelector.model2')}
                  </button>
                  <button
                    onClick={() => setSelectedModel('model3-server')}
                    className={`w-full px-3 py-2 text-sm rounded-md transition-colors ${
                      selectedModel === 'model3-server'
                        ? 'bg-sangre-500 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    {t('photoEvaluation.modelSelector.model3Server')}
                  </button>
                  <button
                    onClick={() => setSelectedModel('model3-live')}
                    className={`w-full px-3 py-2 text-sm rounded-md transition-colors ${
                      selectedModel === 'model3-live'
                        ? 'bg-sangre-500 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    {t('photoEvaluation.modelSelector.model3Live')}
                  </button>
                  <button
                    onClick={() => setSelectedModel('anemia-server')}
                    className={`w-full px-3 py-2 text-sm rounded-md transition-colors ${
                      selectedModel === 'anemia-server'
                        ? 'bg-sangre-500 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    Anemia Detection (Servidor)
                  </button>
                  <button
                    onClick={() => setSelectedModel('anemia-live')}
                    className={`w-full px-3 py-2 text-sm rounded-md transition-colors ${
                      selectedModel === 'anemia-live'
                        ? 'bg-sangre-500 text-white'
                        : 'bg-white text-gray-700 border border-gray-300'
                    }`}
                  >
                    Anemia Detection (Live)
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('photoEvaluation.modelSelector.currentModel')} {
                    selectedModel === 'millev' ? 'Millev (Recomendado)' :
                    selectedModel === 'model2' ? '2 (Detect)' :
                    selectedModel === 'model3-server' ? '3 (Servidor)' :
                    selectedModel === 'model3-live' ? '3 (Live)' :
                    selectedModel === 'anemia-server' ? 'Anemia (Servidor)' :
                    selectedModel === 'anemia-live' ? 'Anemia (Live)' : 'Millev (Recomendado)'
                  }
                </p>
              </div>
            )}
          </div>
          
          <button 
            onClick={proceedToNails}
            className="btn-primary w-full"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : (
              t('photoEvaluation.buttons.continue')
            )}
          </button>
        </div>
      )}
      
      {step === 'nails' && (
        <div className="animate-slide-in-right">
          <CameraView 
            onCapture={handleNailCapture} 
            captureType="nails" 
            previewImage={nailImage} 
          />
          
          <div className="flex flex-col gap-2 my-4">
            <button 
              onClick={() => setShowSamples(!showSamples)}
              className="text-sm text-sangre-600 hover:text-sangre-700 flex items-center justify-center"
            >
              {showSamples ? t('photoEvaluation.sampleImages.hide') : t('photoEvaluation.sampleImages.show')}
            </button>
            
            {showSamples && renderSampleImages('nails')}
          </div>
          
          <button 
            onClick={processImages}
            className="btn-primary w-full"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : (
              t('photoEvaluation.buttons.analyze')
            )}
          </button>
        </div>
      )}
      
      {step === 'processing' && (
        <div className="flex flex-col items-center justify-center h-[70vh] animate-fade-in">
          <div className="card-shadow bg-white p-8 rounded-2xl text-center">
            <Loader2 className="w-16 h-16 text-sangre-500 mx-auto mb-4 animate-spin" />
            <h3 className="text-xl font-medium text-gray-800 mb-2">{t('photoEvaluation.processing.title')}</h3>
            <p className="text-gray-600">
              {t('photoEvaluation.processing.description')}
            </p>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PhotoEvaluationScreen;
